import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n.jsx";
import { marketingHref } from "@/lib/localePath";
import { useSeo } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import api, { isAuthed } from "@/lib/api";
import { ArrowUpRight, Briefcase, Building2, Users, CalendarDays, FileText, Utensils, MapPin, Map as MapIcon, ShieldCheck, Trophy, ClipboardList } from "lucide-react";
import { absUrl } from "@/components/PhotoUploader";
import PropertyMap from "@/components/PropertyMap";
import HeroSearchBar from "@/components/HeroSearchBar";
import CommissionTable from "@/components/CommissionTable";
import SiteFooter from "@/components/SiteFooter";

const IMG = {
  hero: "https://images.unsplash.com/photo-1718301701402-ccd5d73c45f0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxpdGFsaWFuJTIwYmVkJTIwYW5kJTIwYnJlYWtmYXN0fGVufDB8fHx8MTc4NjAyNDgzNXww&ixlib=rb-4.1.0&q=85",
  interior: "https://images.unsplash.com/photo-1505576391880-b3f9d713dc4f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjY2NzN8MHwxfHNlYXJjaHwxfHx0ZXJyYWNvdHRhJTIwaW50ZXJpb3IlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg2MDI0ODM1fDA&ixlib=rb-4.1.0&q=85",
  breakfast: "https://images.unsplash.com/photo-1721821299313-7d0d490f10a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxpdGFsaWFuJTIwYmVkJTIwYW5kJTIwYnJlYWtmYXN0fGVufDB8fHx8MTc4NjAyNDgzNXww&ixlib=rb-4.1.0&q=85",
  sage: "https://images.unsplash.com/photo-1768445338301-dccd39b5923f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxzYWdlJTIwZ3JlZW4lMjBicmVha2Zhc3QlMjB0YWJsZXxlbnwwfHx8fDE3ODYwMjQ4MzV8MA&ixlib=rb-4.1.0&q=85",
};

export default function Landing() {
  const { t, lang } = useLang();
  const IT = lang === "it";
  // La home ha un indirizzo per lingua (`/it`, `/en`) e due titoli: senza
  // questo, le due sezioni erediteranno il canonical di `index.html` e per il
  // motore sarebbero la stessa pagina, con il titolo italiano su entrambe.
  // `path: "/"` e' la chiave della home: il modulo aggiunge il prefisso della
  // lingua in cui la pagina viene servita.
  useSeo({
    title: IT
      ? "Hostaly — Ospitalità italiana, nel mondo."
      : "Hostaly — Italian hospitality, worldwide.",
    description: IT
      ? "Vetrina bilingue, prenotazione diretta senza commissioni, sync OTA e PMS incluso. La piattaforma per B&B, case vacanza e hotel italiani."
      : "Bilingual listings, direct booking with no OTA commission, OTA sync and a built-in PMS. The platform for Italian B&Bs, holiday homes and hotels.",
    path: "/",
    alternates: true,
  });
  const [properties, setProperties] = useState([]);
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.post("/seed").catch(() => {});
    api.get("/properties?limit=6").then((r) => setProperties(r.data)).catch(() => {});
    api.get("/directory/featured?limit=6").then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      {/* HERO */}
      <section className="relative grain-bg overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <div className="uppercase tracking-[0.2em] text-xs text-sage font-semibold mb-6" data-testid="hero-eyebrow">
              {t("landing.eyebrow")}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-ink">
              {t("landing.title1")}<br />
              <span className="italic text-primary">{t("landing.title2")}</span>
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed" data-testid="hero-subtitle">
              {t("landing.subtitle")}
            </p>

            {/* Traveller search bar */}
            <div className="mt-8" data-testid="hero-traveller-block">
              <div className="uppercase tracking-[0.2em] text-[10px] text-primary font-semibold mb-2">
                {IT ? "Sei un viaggiatore? Cerca la tua struttura" : "You're a traveller? Find a stay"}
              </div>
              <HeroSearchBar/>
            </div>

            <div className="mt-8 pt-6 border-t border-border/60">
              <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-3">
                {IT ? "Gestisci una struttura?" : "Do you host?"}
              </div>
              <div className="flex flex-wrap gap-3">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="hero-cta-primary">
                  <Link to="/auth?mode=register">{t("landing.cta_primary")} <ArrowUpRight className="w-4 h-4 ml-1"/></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="lift" data-testid="hero-cta-secondary">
                  <a href="#showcases">{t("landing.cta_secondary")}</a>
                </Button>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6 relative h-[500px] lg:h-[620px]">
            {/* Tetris layout */}
            <div className="absolute top-0 right-0 w-[75%] h-[68%] rounded-lg overflow-hidden shadow-xl">
              <img src={IMG.hero} alt="hero" className="w-full h-full object-cover"/>
            </div>
            <div className="absolute bottom-0 left-0 w-[52%] h-[48%] rounded-lg overflow-hidden shadow-xl border-4 border-background">
              <img src={IMG.breakfast} alt="breakfast" className="w-full h-full object-cover"/>
            </div>
            <div className="absolute top-[38%] left-0 w-[32%] h-[26%] rounded-lg overflow-hidden shadow-lg border-4 border-background hidden lg:block">
              <img src={IMG.sage} alt="sage" className="w-full h-full object-cover"/>
            </div>
          </div>
        </div>
        {/* Marquee stripe */}
        <div className="border-y border-border/60 bg-sand overflow-hidden">
          <div className="flex marquee-track whitespace-nowrap py-3">
            {[..."Positano · Chianti · Cinque Terre · Val d'Orcia · Amalfi · Puglia · Sicilia · Dolomiti · Portofino · Lago di Como".split(" · "), ..."Positano · Chianti · Cinque Terre · Val d'Orcia · Amalfi · Puglia · Sicilia · Dolomiti · Portofino · Lago di Como".split(" · ")].map((c, i) => (
              <span key={i} className="mx-8 text-sm uppercase tracking-[0.25em] text-ink/60 font-medium">{c}</span>
            ))}
          </div>
        </div>
      </section>

      {/* TREASURE HUNTS — placed right under the hero search bar so travellers see the flagship guided-city product first */}
      <TreasureHuntsSection IT={IT}/>

      {/* FEATURES */}
      <section id="network" className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-16">
          <div className="uppercase tracking-[0.2em] text-xs text-sage font-semibold mb-4">— core features</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-ink">
            Non solo una vetrina.<br /><span className="italic text-primary">Una rete.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-12">
          {[
            { icon: Building2, k: "f1" },
            { icon: Users, k: "f2" },
            { icon: Utensils, k: "f3" },
            { icon: CalendarDays, k: "f4" },
            { icon: ClipboardList, k: "f5" },
          ].map(({ icon: Icon, k }) => (
            <div key={k} className="flex gap-5 border-t border-border pt-8" data-testid={`feature-${k}`}>
              <div className="shrink-0 w-12 h-12 rounded-md bg-primary/10 text-primary grid place-items-center">
                <Icon className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-display text-2xl tracking-tight mb-3">{t(`landing.${k}_title`)}</h3>
                <p className="text-muted-foreground leading-relaxed">{t(`landing.${k}_body`)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED (boosted properties) */}
      {featured.length > 0 && (
        <section id="featured" className="max-w-7xl mx-auto px-6 py-16" data-testid="landing-featured">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <div className="uppercase tracking-[0.2em] text-xs text-primary font-semibold mb-3 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary animate-pulse"/>
                {IT ? "In evidenza · Scelte del mese" : "Featured · Editor's picks"}
              </div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight">
                {IT ? "Le strutture consigliate" : "Handpicked stays"}
              </h2>
            </div>
            <Link to={marketingHref("directory", lang)} className="text-sm text-primary hover:underline" data-testid="featured-see-all">
              {IT ? "Vedi tutte →" : "See all →"}
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p) => (
              <Link key={p.id} to={`/p/${p.slug}`} className="group relative block border border-primary/20 rounded-lg overflow-hidden bg-card lift" data-testid={`featured-${p.slug}`}>
                <div className="aspect-[16/10] relative">
                  {p.hero_image && <img src={p.hero_image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>}
                  <div className="absolute top-3 left-3 bg-primary text-primary-foreground text-[10px] uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 shadow">
                    ★ {p.boost_tier === "top" ? (IT ? "Top" : "Top") : p.boost_tier === "premium" ? "Premium" : (IT ? "In evidenza" : "Featured")}
                  </div>
                  {p.verified && (
                    <div className="absolute top-3 right-3 bg-sage text-white text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full shadow">
                      ✓ {IT ? "Verificata" : "Verified"}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{p.city}, {p.region} · {p.property_type}</div>
                  <div className="font-display text-xl group-hover:text-primary transition-colors">{p.name}</div>
                  {p.from_price && (
                    <div className="mt-2 text-xs">
                      <span className="text-muted-foreground">{IT ? "Da" : "From"}</span>{" "}
                      <span className="font-display text-lg text-foreground">€{p.from_price}</span>
                      <span className="text-muted-foreground">/{IT ? "notte" : "night"}</span>
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* SHOWCASES */}
      <section id="showcases" className="bg-sand py-24 grain-bg">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
            <div>
              <div className="uppercase tracking-[0.2em] text-xs text-sage font-semibold mb-3">— {t("landing.showcase_title")}</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-ink">{t("landing.showcase_sub")}</h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="showcase-grid">
            {properties.map((p) => (
              <Link
                key={p.id}
                to={`/p/${p.slug}`}
                data-testid={`showcase-card-${p.slug}`}
                className="group block"
              >
                <div className="relative aspect-[4/5] overflow-hidden img-overlay rounded-lg">
                  <img src={absUrl(p.hero_image) || IMG.hero} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                  {p.verified && (
                    <div
                      className="absolute top-3 right-3 z-20 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] bg-sage text-white rounded-full px-2.5 py-1 shadow-lg"
                      data-testid={`showcase-verified-${p.slug}`}
                      title={lang === "it" ? "Struttura verificata" : "Verified property"}
                    >
                      <ShieldCheck className="w-3 h-3"/>
                      {lang === "it" ? "Verificata" : "Verified"}
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white z-10">
                    <div className="uppercase tracking-[0.2em] text-[10px] opacity-80 mb-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3"/> {p.city}, {p.region}
                    </div>
                    <div className="font-display text-3xl leading-tight">{p.name}</div>
                  </div>
                </div>
                <p className="mt-4 text-sm text-muted-foreground line-clamp-2">
                  {lang === "it" ? p.description_it : p.description_en}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* INTERACTIVE MAP */}
      <PropertyMap/>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-24">        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="uppercase tracking-[0.2em] text-xs text-sage font-semibold mb-4">— {t("landing.how_title")}</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-ink sticky top-24">
              Tre passi<br/><span className="italic text-primary">verso la rete.</span>
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-10">
            {[1, 2, 3].map((n) => (
              <div key={n} className="flex gap-6 border-b border-border pb-8">
                <div className="font-display text-6xl text-primary/40 leading-none w-16 shrink-0">0{n}</div>
                <div>
                  <h3 className="font-display text-3xl tracking-tight mb-2">{t(`landing.how_${n}`)}</h3>
                  <p className="text-muted-foreground leading-relaxed max-w-xl">{t(`landing.how_${n}b`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMISSIONI E PIANI */}
      <section id="commissioni" className="max-w-7xl mx-auto px-6 pb-24" data-testid="landing-commissioni">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <div className="uppercase tracking-[0.2em] text-xs text-sage font-semibold mb-4">
              — Commissioni
            </div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-ink">
              Cosa ti costa<br/><span className="italic text-primary">Hostaly, davvero.</span>
            </h2>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Il numero che interessa a chi ospita è uno solo: quanta parte della
              prenotazione resta a te. Eccolo, per ogni livello.
            </p>
          </div>
          <div className="lg:col-span-8">
            <CommissionTable/>
          </div>
        </div>
      </section>

      {/* TRADE B2B — il canale agenzie, presentato dove lo vedono sia chi
          ospita sia chi compra: la sezione pubblica del canale, non la scheda
          operativa (quella è `/trade` e nella dashboard host). */}
      <TradeSection IT={IT}/>

      {/* ATTIVITÀ LOCALI — la porta di chi non è una struttura: ristoranti,
          botteghe, musei, esperienze. Sta qui e non in fondo perché è il posto
          in cui un host scopre che può presentarci i partner della sua zona
          (e prendersene una quota): senza, la sezione non la troverebbe
          nessuno di quelli che dovrebbero venderla. */}
      <LocalBusinessesBand IT={IT}/>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.interior} alt="" className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-ink/70"></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-6 py-32 text-center text-sand">
          <h2 className="font-display text-5xl sm:text-6xl tracking-tight mb-6">{t("landing.final_title")}</h2>
          <p className="text-lg opacity-90 mb-10 max-w-2xl mx-auto">{t("landing.final_body")}</p>
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="final-cta">
            <Link to="/auth?mode=register">{t("landing.cta_primary")} <ArrowUpRight className="w-4 h-4 ml-1"/></Link>
          </Button>
        </div>
      </section>

      {/* Footer unico (`SiteFooter`): dati societari da `/api/site/identity`,
          non scritti nella pagina. Qui tiene anche il banner social, che è
          della marca e non del documento legale, perciò è una prop. */}
      <SiteFooter social />
    </div>
  );
}

function TradeSection({ IT }) {
  const { lang } = useLang();
  const punti = IT ? [
    { icon: Briefcase, testo: "Pubblichi blocchi di camere con date, prezzo per camera e numero di camere: le agenzie li vedono nella vetrina Trade." },
    { icon: FileText, testo: "Raggruppi più strutture in un pacchetto tour e ne esporti il preventivo in PDF, pronto da mandare al cliente." },
    { icon: Users, testo: "Chi acquista è un'agenzia, un tour operator o un altro hotel: si registra con partita IVA e paga con carta o PayPal." },
    { icon: ShieldCheck, testo: "Fee del 5% sul totale del pacchetto, dichiarata prima del checkout: nessun costo di iscrizione e nessun canone." },
  ] : [
    { icon: Briefcase, testo: "You publish room blocks with dates, price per room and quantity: agencies see them in the trade vitrine." },
    { icon: FileText, testo: "You group several properties into one tour package and export the quote as a PDF, ready to send." },
    { icon: Users, testo: "Buyers are agencies, tour operators or other hotels: they register with a VAT number and pay by card or PayPal." },
    { icon: ShieldCheck, testo: "A 5% fee on the package total, stated before checkout: no sign-up cost, no subscription." },
  ];
  return (
    <section id="trade" className="bg-sand py-24 grain-bg border-y border-border/60" data-testid="landing-trade-section">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="uppercase tracking-[0.2em] text-xs text-sage font-semibold mb-4 flex items-center gap-2">
            <Briefcase className="w-4 h-4"/> {IT ? "Trade B2B" : "Trade B2B"}
          </div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-ink leading-[1.05]" data-testid="trade-heading">
            {IT ? "Le tue camere, anche a chi" : "Your rooms, also to those"}<br/>
            <span className="italic text-primary">{IT ? "le compra in blocco." : "buy them in bulk."}</span>
          </h2>
          <p className="text-muted-foreground mt-5 leading-relaxed">
            {IT
              ? "La vetrina arriva fino a chi prenota una camera alla volta. Il canale Trade è per agenzie, tour operator e altri hotel che prendono dieci camere per una settimana — e che cercano strutture come la tua prima di tutto altrove."
              : "The vitrine reaches whoever books one room at a time. The Trade channel is for agencies, tour operators and other hotels taking ten rooms for a week — and they look for properties like yours everywhere else first."}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="trade-cta-host">
              <Link to="/trade">{IT ? "Vai alla sezione Trade" : "Open the Trade section"} <ArrowUpRight className="w-4 h-4 ml-1"/></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="lift bg-transparent" data-testid="trade-cta-pricing">
              <Link to={marketingHref("pricing", lang)}>{IT ? "Piano Pro" : "Pro plan"}</Link>
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">
            {IT
              ? "Pubblicare blocchi e attivare le forniture B2B richiede un piano Pro o Enterprise. La vetrina delle offerte resta aperta a tutti."
              : "Publishing blocks and enabling B2B supply requires a Pro or Enterprise plan. The offer vitrine stays open to everyone."}
          </p>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-x-8 gap-y-8">
          {punti.map(({ icon: Icon, testo }, i) => (
            <div key={i} className="flex gap-4 border-t border-border pt-6" data-testid={`trade-point-${i}`}>
              <div className="shrink-0 w-11 h-11 rounded-md bg-primary/10 text-primary grid place-items-center">
                <Icon className="w-5 h-5"/>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{testo}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LocalBusinessesBand({ IT }) {
  const { lang } = useLang();
  const punti = IT ? [
    "Ristoranti, botteghe, musei, esperienze e noleggi entrano nella guida della loro città.",
    "Mettono in palio un premio che le mappe del tesoro sbloccano: la ricompensa di un gioco, non un annuncio.",
    "L'hotel che li presenta prende il 15% della loro fee per dodici mesi: è lui il venditore di questa sezione.",
  ] : [
    "Restaurants, shops, museums, experiences and rentals join their city guide.",
    "They put up a prize the treasure hunts unlock: the reward of a game, not an ad.",
    "The hotel referring them takes 15% of their fee for twelve months: it is the sales channel for this section.",
  ];
  return (
    <section id="attivita-locali" className="bg-sand py-24 grain-bg border-y border-border/60" data-testid="landing-local-section">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <div className="uppercase tracking-[0.2em] text-xs text-sage font-semibold mb-4 flex items-center gap-2">
            <Utensils className="w-4 h-4"/> {IT ? "Attività locali" : "Local businesses"}
          </div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-ink leading-[1.05]" data-testid="landing-local-heading">
            {IT ? "La città intorno all'hotel," : "The town around the hotel,"}<br/>
            <span className="italic text-primary">{IT ? "dentro la guida." : "inside the guide."}</span>
          </h2>
          <p className="text-muted-foreground mt-5 leading-relaxed">
            {IT
              ? "Le guide e le mappe hanno bisogno di posti veri dove fermarsi e di premi veri da vincere. Un premio senza nessuno che lo offra è un premio che il vincitore scopre non esistere: per questo le attività di zona si iscrivono, si fanno verificare e mettono a disposizione quello che sanno fare."
              : "Guides and maps need real places to stop and real prizes to win. A prize with nobody offering it is a prize the winner finds out does not exist: that is why local businesses sign up, get verified and offer what they do."}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="landing-local-cta">
              <Link to={marketingHref("localPartners", lang)}>{IT ? "Iscrivi la tua attività" : "Sign your business up"} <ArrowUpRight className="w-4 h-4 ml-1"/></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="lift bg-transparent" data-testid="landing-local-hunts">
              <Link to="/caccia-al-tesoro">{IT ? "Vedi le mappe" : "See the maps"}</Link>
            </Button>
          </div>
        </div>
        <div className="lg:col-span-7 space-y-6">
          {punti.map((testo, i) => (
            <div key={i} className="flex gap-4 border-t border-border pt-6" data-testid={`landing-local-point-${i}`}>
              <div className="shrink-0 w-11 h-11 rounded-md bg-primary/10 text-primary grid place-items-center">
                {i === 0 ? <Utensils className="w-5 h-5"/> : i === 1 ? <Trophy className="w-5 h-5"/> : <Users className="w-5 h-5"/>}
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{testo}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TreasureHuntsSection({ IT }) {
  const [hunts, setHunts] = useState([]);
  const [leader, setLeader] = useState(null);
  const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/hunts").then((r) => setHunts(r.data || [])).catch(() => {});
    api.get("/hunts/leaderboard/weekly").then((r) => setLeader(r.data?.leader || null)).catch(() => {});
    // Personal claim only for signed-in guests with activity this week.
    // Failures are swallowed silently so the anonymous banner keeps working.
    if (isAuthed()) {
      api.get("/hunts/leaderboard/weekly/me")
        .then((r) => setMe(r.data?.has_activity ? r.data : null))
        .catch(() => setMe(null));
    }
  }, []);
  if (!hunts.length) return null;
  return (
    <section className="max-w-6xl mx-auto px-6 py-24" data-testid="landing-hunts-section">
      <div className="text-center mb-14">
        <div className="text-[11px] uppercase tracking-[0.35em] text-primary mb-3 font-semibold">
          {IT ? "Mappa del Tesoro" : "Treasure Hunts"}
        </div>
        <h2 className="font-display font-normal italic" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
          {IT ? "Racconti che si vivono a piedi" : "Stories you walk into"}
        </h2>
        <p className="text-muted-foreground italic mt-4 max-w-xl mx-auto">
          {IT
            ? "Sette segreti + sette tavole d'autore. Percorsi cittadini pensati per chi vuole andare oltre le cartoline."
            : "Seven secrets + seven signature tables. City walks for travellers who want more than postcards."}
        </p>
      </div>
      {(me || leader) && (
        <div className="max-w-2xl mx-auto mb-10 flex flex-col sm:flex-row items-center justify-center gap-2 text-sm">
          {leader && (
            <div className="flex items-center gap-2.5 bg-primary/5 border border-primary/30 rounded-full px-5 py-2.5" data-testid="hunts-weekly-leader">
              <Trophy className="w-4 h-4 text-primary shrink-0"/>
              <span>
                <b className="text-primary">{leader.city}</b>{" "}
                {IT
                  ? <>è in testa questa settimana con <b>{leader.count}</b> completamenti</>
                  : <>leads this week with <b>{leader.count}</b> completions</>}
              </span>
            </div>
          )}
          {me && (
            <div className="flex items-center gap-2.5 bg-sage/10 border border-sage/40 rounded-full px-5 py-2.5" data-testid="hunts-weekly-me">
              <span className="w-2 h-2 rounded-full bg-sage animate-pulse shrink-0"/>
              <span>
                {IT
                  ? <>Sei il <b className="text-sage">#{me.rank}</b> su <b>{me.total}</b> questa settimana a <b>{me.city}</b></>
                  : <>You're <b className="text-sage">#{me.rank}</b> of <b>{me.total}</b> this week in <b>{me.city}</b></>}
              </span>
            </div>
          )}
        </div>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {hunts.slice(0, 6).map((h) => (
          <Link key={h.id} to={`/citta/${encodeURIComponent((h.city || "").toLowerCase())}`}
                className="group block relative aspect-[4/5] overflow-hidden rounded-sm shadow-lg" data-testid={`hunt-card-${h.id}`}>
            <img src={h.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(35,31,27,0.15) 30%, rgba(35,31,27,0.9) 100%)" }}/>
            <div className="absolute inset-x-0 bottom-0 p-6 text-white">
              <div className="text-[10px] uppercase tracking-[0.35em] opacity-80 mb-2">{h.city}</div>
              <div className="font-display italic text-3xl leading-tight">{h.title}</div>
              <div className="text-sm opacity-85 italic mt-2 line-clamp-2">{h.subtitle}</div>
              {h.completed_this_month > 0 && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-2.5 py-1" data-testid={`hunt-count-${h.id}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"/>
                  {IT
                    ? `${h.completed_this_month} viaggiatori l'hanno completata questo mese`
                    : `${h.completed_this_month} travellers completed it this month`}
                </div>
              )}
              <div className="text-xs mt-4 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all inline-flex items-center gap-1">
                {IT ? "Scopri la città" : "Discover the city"} <ArrowUpRight className="w-3 h-3"/>
              </div>
            </div>
          </Link>
        ))}
      </div>
      {hunts.length > 6 && (
        <div className="text-center mt-12">
          <Link
            to="/caccia-al-tesoro"
            className="inline-flex items-center gap-2 text-sm px-6 py-3 rounded-full border border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground transition-all"
            data-testid="landing-hunts-all-link"
          >
            <MapIcon className="w-4 h-4"/>
            {IT
              ? `Tutte le mappe (${hunts.length} città)`
              : `All maps (${hunts.length} cities)`}
            <ArrowUpRight className="w-3.5 h-3.5"/>
          </Link>
        </div>
      )}
    </section>
  );
}
